import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthState } from '@/hooks/useAuthState';
import AdminLayout from '@/layouts/AdminLayout';
import { 
  BarChart3, 
  Users, 
  Calendar, 
  CheckCircle, 
  Home, 
  CalendarDays,
  TrendingUp,
  User,
  Loader2,
  XCircle,
  Clock,
  Ticket,
  CreditCard,
  Crown,
  Building2
} from 'lucide-react';
import { getPlanMeta, normalizePlanSlug } from '@/lib/subscriptionPlanMeta';
import { cn } from '@/lib/utils';
import { DashboardService, DashboardStats } from '@/services/dashboardService';
import { getApiEndpoint } from '@/config/api';
import { useToast } from '@/hooks/use-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || userType !== 'admin') {
      setLoading(false);
      return;
    }
    loadDashboardStats();
  }, [authLoading, isAuthenticated, userType]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
        if (!token) {
        toast({
          title: 'Erro de Autenticação',
          description: 'Você precisa estar logado para acessar o dashboard.',
          variant: 'destructive'
        });
        navigate('/admin/login');
        return;
      }

      const response = await fetch(getApiEndpoint('/api/dashboard/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        console.error('Erro na resposta:', response.status, errorData);
        throw new Error(errorData.error || `Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setStats(data);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Não foi possível carregar as estatísticas do dashboard.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!stats) {
    return (
      <AdminLayout>
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Nenhum dado disponível.</p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Dashboard Administrativo</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Visão geral do sistema</p>
        </div>

        {/* Seção Administração */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6" />
            Administração
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6">
            {/* Gráfico de Reservas */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Status das Reservas
                </CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Total */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground">Total</span>
                      <span className="text-lg font-bold">{stats.totalBookingRequests}</span>
                    </div>
                  </div>

                  {/* Aceitas */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-muted-foreground">Aceitas</span>
                      </div>
                      <span className="text-lg font-bold text-green-600">{stats.acceptedBookingRequests}</span>
                    </div>
                    {stats.totalBookingRequests > 0 && (
                      <div className="w-full bg-muted rounded-full h-2.5">
                        <div
                          className="bg-green-600 h-2.5 rounded-full transition-all"
                          style={{
                            width: `${(stats.acceptedBookingRequests / stats.totalBookingRequests) * 100}%`
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Rejeitadas */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-600" />
                        <span className="text-sm text-muted-foreground">Rejeitadas</span>
                      </div>
                      <span className="text-lg font-bold text-red-600">{stats.rejectedBookingRequests}</span>
                    </div>
                    {stats.totalBookingRequests > 0 && (
                      <div className="w-full bg-muted rounded-full h-2.5">
                        <div
                          className="bg-red-600 h-2.5 rounded-full transition-all"
                          style={{
                            width: `${(stats.rejectedBookingRequests / stats.totalBookingRequests) * 100}%`
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Pendentes */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm text-muted-foreground">Pendentes</span>
                      </div>
                      <span className="text-lg font-bold text-yellow-600">
                        {stats.totalBookingRequests - stats.acceptedBookingRequests - stats.rejectedBookingRequests}
                      </span>
                    </div>
                    {stats.totalBookingRequests > 0 && (
                      <div className="w-full bg-muted rounded-full h-2.5">
                        <div
                          className="bg-yellow-600 h-2.5 rounded-full transition-all"
                          style={{
                            width: `${((stats.totalBookingRequests - stats.acceptedBookingRequests - stats.rejectedBookingRequests) / stats.totalBookingRequests) * 100}%`
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Novos Usuários e Imóveis */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Novos Usuários e Imóveis
                </CardTitle>
                <div className="flex gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <Home className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Usuários */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Usuários</span>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats.newUsersToday}</div>
                      <p className="text-xs text-muted-foreground">Hoje</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <div>
                        <div className="text-lg font-semibold">{stats.newUsersThisMonth}</div>
                        <p className="text-xs text-muted-foreground">Este mês</p>
                      </div>
                      <div>
                        <div className="text-lg font-semibold">{stats.newUsersThisYear}</div>
                        <p className="text-xs text-muted-foreground">Este ano</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Imóveis */}
                  <div className="space-y-2 pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Imóveis</span>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats.newPropertiesToday}</div>
                      <p className="text-xs text-muted-foreground">Hoje</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <div>
                        <div className="text-lg font-semibold">{stats.newPropertiesThisMonth}</div>
                        <p className="text-xs text-muted-foreground">Este mês</p>
                      </div>
                      <div>
                        <div className="text-lg font-semibold">{stats.newPropertiesThisYear}</div>
                        <p className="text-xs text-muted-foreground">Este ano</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tokens Mais Indicados */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Tokens Mais Indicados
                </CardTitle>
                <Ticket className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {stats.topReferralTokens && stats.topReferralTokens.length > 0 ? (
                  <div className="space-y-2 sm:space-y-3">
                    {stats.topReferralTokens.map((item) => (
                      <div key={item.token} className="flex items-start justify-between gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-mono font-medium break-all leading-tight">{item.token}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-base sm:text-lg font-bold">{item.count}</div>
                          <p className="text-xs text-muted-foreground">indicações</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum token de indicação encontrado</p>
                )}
              </CardContent>
            </Card>

            {/* Estatísticas de Imóveis e Disponibilidades */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Estatísticas de Imóveis
                </CardTitle>
                <Home className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Datas Disponíveis */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Datas Disponíveis</span>
                      </div>
                      <span className="text-lg font-bold">{stats.totalAvailabilityPeriods}</span>
                    </div>
                  </div>

                  {/* Valor Médio da Diária */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Valor Médio Diária</span>
                      </div>
                      <span className="text-lg font-bold">
                        {stats.averagePricePerNight > 0 
                          ? `R$ ${stats.averagePricePerNight.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : 'R$ 0,00'
                        }
                      </span>
                    </div>
                  </div>

                  {/* Mês com Mais Disponibilidade */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Mês com Mais Disponibilidade</span>
                      </div>
                      {stats.monthWithMostAvailability ? (
                        <div className="text-right">
                          <div className="text-sm font-bold capitalize">
                            {stats.monthWithMostAvailability.monthName}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {stats.monthWithMostAvailability.count} períodos
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-3 mt-3">
                    {/* Imóvel Mais Barato */}
                    {stats.cheapestProperty && (
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-green-600">Mais Barato</span>
                          <span className="text-sm font-bold text-green-600">
                            R$ {stats.cheapestProperty.pricePerNight.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <Link
                          to={`/imovel/${stats.cheapestProperty.propertyId}`}
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          {stats.cheapestProperty.propertyTitle}
                          <span className="text-[10px]">↗</span>
                        </Link>
                      </div>
                    )}

                    {/* Imóvel Mais Caro */}
                    {stats.mostExpensiveProperty && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-red-600">Mais Caro</span>
                          <span className="text-sm font-bold text-red-600">
                            R$ {stats.mostExpensiveProperty.pricePerNight.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                        <Link
                          to={`/imovel/${stats.mostExpensiveProperty.propertyId}`}
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          {stats.mostExpensiveProperty.propertyTitle}
                          <span className="text-[10px]">↗</span>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Proprietários por plano */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Proprietários por plano
                </CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  Contagem apenas de usuários do tipo proprietário.
                </p>
                {stats.ownersByPlan && stats.ownersByPlan.length > 0 ? (
                  <div className="space-y-2">
                    {stats.ownersByPlan.map((item) => {
                      const slug = normalizePlanSlug(item.planSlug, item.planId);
                      const meta = getPlanMeta(item.planSlug, item.planId);
                      return (
                        <div
                          key={item.planId}
                          className={cn(
                            'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5',
                            'whitespace-nowrap',
                            meta.badgeClassName,
                          )}
                        >
                          <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
                            {slug === 'live' && <Crown className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                            {slug === 'porto2life' && (
                              <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            )}
                            {slug === 'freemium' && <User className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                            <span className="truncate">{meta.shortLabel}</span>
                          </span>
                          <span className="shrink-0 text-lg font-bold tabular-nums">{item.count}</span>
                        </div>
                      );
                    })}
                    <div className="flex w-full items-center justify-between gap-3 rounded-lg border bg-muted/50 px-3 py-2.5 whitespace-nowrap">
                      <span className="text-sm font-semibold text-muted-foreground">Total</span>
                      <span className="shrink-0 text-lg font-bold tabular-nums">{stats.ownersTotal ?? 0}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum proprietário cadastrado</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Seção Controle */}
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6" />
            Controle
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Usuários com mais imóveis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  Usuários com Mais Imóveis
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.topUsersByProperties.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum dado disponível</p>
                ) : (
                  <div className="space-y-4">
                    {stats.topUsersByProperties.map((user, index) => (
                      <div key={user.userId} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user.userName}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.userEmail}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-lg font-bold">{user.propertiesCount}</div>
                          <p className="text-xs text-muted-foreground">imóveis</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Usuários com mais datas disponíveis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="h-5 w-5" />
                  Usuários com Mais Datas Disponíveis
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.topUsersByAvailability.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum dado disponível</p>
                ) : (
                  <div className="space-y-4">
                    {stats.topUsersByAvailability.map((user, index) => (
                      <div key={user.userId} className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user.userName}</p>
                            <p className="text-xs text-muted-foreground truncate">{user.userEmail}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-lg font-bold">{user.availabilityCount}</div>
                          <p className="text-xs text-muted-foreground">períodos</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;

